
import { useEffect, useRef } from "react";
import { Node, Edge } from "@xyflow/react";
import { NodeData } from "../initial-nodes";

export function useDiagramHistorySync(
  nodes: Node<NodeData>[], 
  edges: Edge[], 
  addToHistory: (nodes: Node<NodeData>[], edges: Edge[]) => void,
  isApplyingHistoryRef?: React.MutableRefObject<boolean>
) {
  const previousStateRef = useRef<{ nodes: Node<NodeData>[], edges: Edge[] } | null>(null);

  // Use a debounced save to history to reduce performance impact
  useEffect(() => {
    // Don't add to history if we're applying an undo/redo operation
    if (isApplyingHistoryRef?.current) {
      previousStateRef.current = { nodes, edges };
      return;
    }

    const timer = setTimeout(() => {
      // Check if state actually changed
      if (previousStateRef.current) {
        const nodesMatch = JSON.stringify(previousStateRef.current.nodes) === JSON.stringify(nodes);
        const edgesMatch = JSON.stringify(previousStateRef.current.edges) === JSON.stringify(edges);
        if (nodesMatch && edgesMatch) {
          return; // No changes, don't add to history
        }
      }

      // Clone nodes and edges to avoid reference issues
      const nodesCopy = JSON.parse(JSON.stringify(nodes));
      const edgesCopy = JSON.parse(JSON.stringify(edges));
      addToHistory(nodesCopy, edgesCopy);
      
      // Update previous state
      previousStateRef.current = { nodes, edges };
    }, 300);

    return () => clearTimeout(timer);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [nodes, edges]);
}
